import { calcularSalario, overallDoValor } from './financas';
import type { Rng } from './rng';
import { VALORES_MERCADO_EUR } from './valores-mercado';
import type { Jogador } from './tipos';

/**
 * Valor de mercado real (Transfermarkt, em euros) usado como base da força e do passe dos jogadores da Série A.
 * Regra do jogo: €1 milhão vale R$ 1 milhão (conversão 1:1). A força vem do valor (ver financas.overallDoValor), então um
 * craque vale e rende mais que um desconhecido, em qualquer clube.
 */
export const EURO_EM_REAIS = 1;

export const normalizarNome = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

function distancia(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) (d[0] as number[])[j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      (d[i] as number[])[j] = Math.min((d[i - 1] as number[])[j]! + 1, (d[i] as number[])[j - 1]! + 1, (d[i - 1] as number[])[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
  return (d[a.length] as number[])[b.length] as number;
}

/** Quão provável é que dois nomes (de fontes diferentes) sejam a mesma pessoa; 0 = não parecem. */
function parecido(a: string, b: string): number {
  if (a === b) return 100;
  const ta = a.split(' ');
  const tb = b.split(' ');
  const [curto, longo] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
  if (curto.every((t) => longo.includes(t))) return 80 - (longo.length - curto.length);
  const dist = distancia(a, b);
  if (dist <= 2 && Math.min(a.length, b.length) >= 6) return 70 - dist;
  if (ta.length > 1 && tb.length > 1 && ta[0] === tb[0] && ta[ta.length - 1] === tb[tb.length - 1]) return 60;
  return 0;
}

/**
 * Liga cada jogador do jogo ao valor da planilha: primeiro o nome igual, depois os mais parecidos (Wikipédia e Transfermarkt
 * escrevem nomes diferentes: "Luiz Benedetti"/"Luis Benedetti"). Cada linha da planilha vale para um só jogador.
 * Devolve o valor em euros de cada nome, na ordem, ou null para quem ficou sem par.
 */
export function parearValores(nomes: string[], planilha: [string, number][]): (number | null)[] {
  const jogo = nomes.map(normalizarNome);
  const fonte = planilha.map(([n]) => normalizarNome(n));
  const resultado: (number | null)[] = nomes.map(() => null);
  const usadoJ = new Set<number>();
  const usadoF = new Set<number>();
  const pares: { j: number; f: number; nota: number }[] = [];
  jogo.forEach((nj, j) => fonte.forEach((nf, f) => {
    const nota = parecido(nj, nf);
    if (nota > 0) pares.push({ j, f, nota });
  }));
  // melhores notas primeiro; em empate vale a ordem do elenco e da planilha (nomes repetidos no mesmo clube)
  pares.sort((x, y) => y.nota - x.nota || x.j - y.j || x.f - y.f);
  for (const p of pares) {
    if (usadoJ.has(p.j) || usadoF.has(p.f)) continue;
    usadoJ.add(p.j);
    usadoF.add(p.f);
    resultado[p.j] = (planilha[p.f] as [string, number])[1];
  }
  return resultado;
}

/** Faixa de valor (R$) de quem não tem valor no Transfermarkt: jogadores novos, da base ou recém-chegados. */
export const VALOR_SEM_MERCADO_MIN = 900_000;
export const VALOR_SEM_MERCADO_MAX = 5_000_000;

/**
 * Valor de quem não tem valor de mercado real: sorteado entre R$ 900 mil e R$ 5 mi, sempre o mesmo para o mesmo nome
 * (hash do nome, sem consumir o Rng do jogo). A força sai desse valor por overallDoValor.
 */
export function valorSemMercado(nome: string): number {
  let h = 2166136261;
  for (const c of normalizarNome(nome)) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  const t = h / 4294967296;
  return Math.round((VALOR_SEM_MERCADO_MIN + t * (VALOR_SEM_MERCADO_MAX - VALOR_SEM_MERCADO_MIN)) / 10_000) * 10_000;
}

/**
 * Ajusta força, valor e salário dos jogadores de um clube da Série A pelo valor de mercado da planilha. Quem não está na planilha
 * (base, recém-chegados) recebe um valor entre R$ 900 mil e R$ 5 mi (valorSemMercado) e a força sai desse valor.
 */
export function aplicarValoresDeMercado(sigla: string, elenco: Jogador[], nivelClube: number, rng: Rng): void {
  const planilha = VALORES_MERCADO_EUR[sigla];
  if (!planilha) return;
  const valores = parearValores(elenco.map((j) => j.nome), planilha);
  const conhecidos: number[] = [];
  elenco.forEach((j, i) => {
    const euros = valores[i];
    if (euros === null || euros === undefined) return;
    j.valor = Math.round((euros * EURO_EM_REAIS) / 1000) * 1000;
    j.overall = overallDoValor(j.valor, j.idade, j.posicao);
    conhecidos.push(j.overall);
  });
  elenco.forEach((j, i) => {
    if (valores[i] === null || valores[i] === undefined) {
      j.valor = valorSemMercado(j.nome);
      j.overall = overallDoValor(j.valor, j.idade, j.posicao);
      j.dadosEstimados = [j.dadosEstimados, 'valor'].filter(Boolean).join(',');
    }
    j.salario = calcularSalario(j.overall, j.idade, j.posicao, nivelClube);
  });
}
